import { ColDef, ICellRendererParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import {
  formatNotificationSettings,
  notificationSettingsCellRenderer,
} from 'components/column-renderers/notificationSettings'
import { createCohortsNamesColumn, createCohortsNamesCsvColumn } from 'features/Cohorts'
import capitalize from 'lodash/capitalize'
import {
  careLevels,
  educationOptions,
  highestDegreeOptions,
  racialDesignationOptions,
  sexOptions,
  yearsOfExperience,
} from 'models/user.model'
import {
  CaregiverRow,
  ELicenseType,
  EPartnerCodeType,
  EPartnerCodeTypeLabels,
  EUserAgeGroups,
  EUserSex,
  OrganizationalUser,
  OwnedAccessCode,
  TraineeRow,
  UserAgeGroupsLabels,
  UserTypeLabels,
  UserTypeTab,
} from 'types'
import { capitalizeFirstLetter, formatDate, isDefined } from 'utils'
import { TraineeStatusBadge } from '../TraineeStatusBadge/TraineeStatusBadge'
import {
  createSubscriptionStatusColumn,
  getSubscriptionStatusText,
  SubscriptionStatusCell,
} from '../../../Subscription/components/SubscriptionStatusColumn'
import { createTeamColumn } from '../../../Team/models/TeamColumn'
import { createTeamRoleColumn, createTeamRoleCsvColumn } from '../../../Team/models/TeamRoleColumn'
import { UserOrgAccessCodesCell } from '../UserOrgAccessCodesCell/UserOrgAccessCodesCell'
import { UserPromoCodesCell } from '../UserPromoCodesCell'

const personalStart: ColDef[] = [
  { headerName: 'First Name', field: 'firstname', filter: 'agTextColumnFilter' },
  { headerName: 'Last name', field: 'lastname', filter: 'agTextColumnFilter' },
  { headerName: 'Email', field: 'email', filter: 'agTextColumnFilter', minWidth: 270 },
]

const personalEnd: ColDef[] = [
  {
    headerName: 'Mobile Phone Number',
    field: 'userBasicInfo.phoneNumber',
    sortable: false,
    minWidth: 220,
  },
  { headerName: 'Date of Birth', field: 'userBasicInfo.dateOfBirth' },
  {
    headerName: 'Age Groups',
    field: 'ageGroups',
    cellRenderer: (props: ICellRendererParams<{ ageGroups: EUserAgeGroups }>) => {
      const ageGroups = props.data?.ageGroups
      if (isDefined(ageGroups)) {
        return UserAgeGroupsLabels[ageGroups]
      }
      return PLACEHOLDER
    },
  },
  { headerName: 'Sex', field: 'userBasicInfo.gender' },
]

const address = [
  { headerName: 'Street Address', field: 'userBasicInfo.street', sortable: false },
  { headerName: 'ZIP Code', field: 'userBasicInfo.zipCode', sortable: false },
  { headerName: 'City', field: 'userBasicInfo.city', sortable: false },
  { headerName: 'State', field: 'userBasicInfo.state', sortable: false },
  { headerName: 'Country', field: 'userBasicInfo.country', sortable: false },
]

const personOfConcern = {
  headerName: 'Person of Concern Full Name',
  field: 'personOfConcernFullName',
  minWidth: 320,
  cellRenderer: (
    props: ICellRendererParams<{
      personOfConcernFirstName: string
      personOfConcernLastName: string
    }>,
  ) => {
    const personOfConcernFirstName = props.data?.personOfConcernFirstName
    const personOfConcernLastName = props.data?.personOfConcernLastName
    if (isDefined(personOfConcernFirstName) && isDefined(personOfConcernLastName)) {
      return `${personOfConcernFirstName} ${personOfConcernLastName}`
    }
    return PLACEHOLDER
  },
}

const levelOfCare = {
  headerName: 'Level of Care',
  field: 'careLevel',
  minWidth: 320,
  cellRenderer: (props: ICellRendererParams<{ careLevel: string }>) => {
    const careLevel = props.data?.careLevel
    if (isDefined(careLevel)) {
      return careLevels.find((it) => it.value === careLevel)?.label || 'Unknown'
    }
    return PLACEHOLDER
  },
}

const totalEngagementColumn = {
  headerName: 'Total Time Online',
  field: 'totalEngagement',
  minWidth: 220,
  sortable: true,
}

const userStatusColumn: ColDef = {
  headerName: 'User Status',
  field: 'userStatus',
  cellRenderer: columnRenderers.capitalizeRenderer('userStatus'),
}

const isIndividualTraineeRow = (row: TraineeRow) => row.licenseType === ELicenseType.individual

/**
 * Trainee status column. Individual trainees pay for their own licence, so they get the same
 * payment status and payment-history link as Family Caregivers; organization-based trainees keep
 * the colored badge (Pending/Active/Inactive).
 */
const traineeStatusColumn: ColDef = {
  headerName: 'User Status',
  field: 'userStatus',
  cellRenderer: (props: ICellRendererParams<TraineeRow>) => {
    const row = props.data

    if (row && isIndividualTraineeRow(row)) {
      return <SubscriptionStatusCell data={row} />
    }

    return <TraineeStatusBadge status={row?.userStatus} />
  },
}

const csvUserStatusColumn = {
  label: 'User Status',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: ({ userStatus }: any) => {
    return userStatus ? capitalize(userStatus) : PLACEHOLDER
  },
}

const csvTraineeStatusColumn = {
  label: 'User Status',
  value: (row: TraineeRow) => {
    if (row && isIndividualTraineeRow(row)) {
      return getSubscriptionStatusText(row) || PLACEHOLDER
    }

    return row?.userStatus ? capitalize(row.userStatus) : PLACEHOLDER
  },
}

const yesNo = (value?: boolean | null) => {
  if (value == null) return PLACEHOLDER
  return value ? 'Yes' : 'No'
}

const caregiverColumnDefs = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  ...personalStart,
  createCohortsNamesColumn('cohortNames'),
  createSubscriptionStatusColumn(),
  totalEngagementColumn,
  {
    headerName: 'Promo Code',
    field: 'accessCodeCode',
    minWidth: 220,
    // One code shows its value; several show a count that opens the list (PAMS-2053).
    cellRenderer: (props: ICellRendererParams<CaregiverRow>) => {
      if (!props.data) return PLACEHOLDER

      const { promoCodes, accessCodeCode } = props.data
      if (promoCodes && promoCodes.length > 1) {
        return <UserPromoCodesCell promoCodes={promoCodes} />
      }

      return promoCodes?.[0]?.code || accessCodeCode || PLACEHOLDER
    },
  },
  {
    headerName: 'Promo Code Effective Date',
    cellRenderer: dateRenderer('accessCodeEffectiveDate'),
    field: 'accessCodeEffectiveDate',
    minWidth: 260,
  },
  {
    headerName: 'Partner Code',
    field: 'partnerCodeCode',
    minWidth: 220,
    filter: 'agTextColumnFilter',
  },
  // Partner and subscription fields are aliases and SQL literals in the backend query, not
  // user columns — sorting by them would hit ORDER BY on a column that does not exist.
  {
    headerName: 'Partner Code Type',
    field: 'partnerCodeType',
    minWidth: 180,
    sortable: false,
    valueFormatter: ({ value }: { value?: EPartnerCodeType | null }) =>
      value ? EPartnerCodeTypeLabels[value] ?? value : PLACEHOLDER,
  },
  {
    headerName: 'Funded',
    field: 'partnerFunded',
    minWidth: 120,
    sortable: false,
    valueFormatter: ({ value }: { value?: boolean | null }) => yesNo(value),
  },
  {
    headerName: 'Partner Organization',
    field: 'partnerOrganization',
    minWidth: 220,
    sortable: false,
  },
  {
    headerName: 'Subscription Activation Date',
    cellRenderer: dateRenderer('subscriptionActivationDate'),
    field: 'subscriptionActivationDate',
    minWidth: 240,
    sortable: false,
  },
  {
    headerName: 'Subscription Expiration Date',
    cellRenderer: dateRenderer('caregiverExpirationDate'),
    field: 'caregiverExpirationDate',
    minWidth: 240,
    sortable: false,
  },
  ...address,
  personOfConcern,
  levelOfCare,
  createTeamRoleColumn(),
  ...personalEnd,
]

const traineeColumnDefs = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  ...personalStart,
  createCohortsNamesColumn('cohortNames'),
  traineeStatusColumn,
  createTeamColumn(),
  createTeamRoleColumn(),
  {
    headerName: 'Survey Monkey - Education',
    field: 'highestDegree',
    cellRenderer: (props: ICellRendererParams<{ [highestDegree: string]: string }>) => {
      const highestDegree = props.data?.['highestDegree']
      if (isDefined(highestDegree)) {
        return (
          highestDegreeOptions.find((it) => it.value === highestDegree)?.tableValue || 'Unknown'
        )
      }
      return PLACEHOLDER
    },
    minWidth: 260,
  },
  {
    headerName: 'Survey Monkey - Race',
    field: 'racialDesignation',
    cellRenderer: (props: ICellRendererParams<{ [racialDesignation: string]: string }>) => {
      const racialDesignation = props.data?.['racialDesignation']
      if (isDefined(racialDesignation)) {
        return (
          racialDesignationOptions.find((it) => it.value === racialDesignation)?.tableValue ||
          'Unknown'
        )
      }
      return PLACEHOLDER
    },
    minWidth: 220,
  },
  {
    headerName: 'Survey Monkey - Job title',
    field: 'jobTitle',
    cellRenderer: (props: ICellRendererParams<{ [jobTitle: string]: string }>) => {
      const jobTitle = props.data?.['jobTitle']
      return jobTitle || PLACEHOLDER
    },
    minWidth: 240,
  },
  {
    headerName: 'Survey Monkey - Age',
    field: 'surveyMonkeyAge',
    minWidth: 240,
  },
  {
    headerName: 'Survey Monkey - Current Age',
    field: 'surveyMonkeyCurrentAge',
    minWidth: 280,
  },
  {
    headerName: 'Survey Monkey - Sex',
    field: 'surveyMonkeySex',
    cellRenderer: (
      props: ICellRendererParams<{ surveyMonkeySex?: string; otherSurveyMonkeySex?: string }>,
    ) => {
      if (!props.data) return PLACEHOLDER
      const { surveyMonkeySex, otherSurveyMonkeySex } = props.data
      if (isDefined(surveyMonkeySex)) {
        if (surveyMonkeySex === EUserSex.other && otherSurveyMonkeySex) {
          return otherSurveyMonkeySex
        }
        return sexOptions.find((item) => item.value === surveyMonkeySex)?.tableValue || 'Unknown'
      }
      return PLACEHOLDER
    },
    minWidth: 240,
  },
  {
    headerName: 'Survey Monkey - Date',
    field: 'surveyMonkeyDate',
    cellRenderer: dateRenderer('surveyMonkeyDate'),
    minWidth: 240,
  },
  {
    headerName: 'Last Notification',
    field: 'lastNotificationSentAt',
    cellRenderer: dateRenderer('lastNotificationSentAt'),
    minWidth: 240,
  },
  {
    headerName: 'Notification Settings',
    field: 'notificationDelivery',
    cellRenderer: notificationSettingsCellRenderer,
    minWidth: 280,
  },
  totalEngagementColumn,
  {
    headerName: 'Institutional Access Code',
    field: 'accessCodeCode',
    cellRenderer: (
      props: ICellRendererParams<{
        accessCodeCode: string
        accessCodeHistory: OwnedAccessCode[]
      }>,
    ) => {
      if (!props.data) return PLACEHOLDER

      const { accessCodeCode, accessCodeHistory } = props.data
      if (accessCodeHistory && accessCodeHistory.length > 0) {
        return (
          <UserOrgAccessCodesCell
            accessCodes={accessCodeHistory}
            showOrgName
            showNumbersOfUsers={false}
            showOwnership
          />
        )
      }

      return accessCodeCode || PLACEHOLDER
    },
    minWidth: 220,
  },
  {
    headerName: 'Organization Type',
    field: 'accessCodeOrganizationType',
    minWidth: 200,
  },
  {
    headerName: 'Training Pathway',
    field: 'trainingPathwayName',
    cellRenderer: (props: ICellRendererParams<{ trainingPathwayName: string }>) => {
      if (!props.data) return PLACEHOLDER

      const { trainingPathwayName } = props.data
      if (isDefined(trainingPathwayName)) {
        const specialPathways = ['Trainee 1A', 'Trainee 2A', 'Trainee 1B', 'Trainee 2B']
        if (specialPathways.includes(trainingPathwayName)) {
          return trainingPathwayName.replace('Trainee ', '')
        }
        return trainingPathwayName
      }

      return PLACEHOLDER
    },
    minWidth: 220,
  },
  {
    headerName: 'Years of Experience',
    field: 'experience',
    cellRenderer: (props: ICellRendererParams<{ [experience: string]: string }>) => {
      const experience = props.data?.['experience']
      if (isDefined(experience)) {
        return yearsOfExperience.find((it) => it.value === experience)?.label || 'Unknown'
      }
      return PLACEHOLDER
    },
  },
  {
    headerName: 'Education',
    field: 'education',
    filter: 'agTextColumnFilter',
    cellRenderer: (props: ICellRendererParams<{ education: string }>) => {
      const education = props.data?.education
      if (isDefined(education)) {
        return educationOptions.find((it) => it.value === education)?.label || 'Unknown'
      }
      return PLACEHOLDER
    },
  },
  {
    headerName: 'Language Preference',
    field: 'language',
    cellRenderer: (props: ICellRendererParams<{ [language: string]: string }>) => {
      const language = props.data?.['language']
      return language ? capitalizeFirstLetter(language) : PLACEHOLDER
    },
    minWidth: 230,
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Access Code Expiry Date',
    cellRenderer: dateRenderer('accessCodeExpiryDate'),
    field: 'accessCodeExpiryDate',
    colId: 'accessCodeExpiryDate',
    minWidth: 250,
  },
  {
    headerName: 'Access Code Effective Date',
    cellRenderer: dateRenderer('accessCodeEffectiveDate'),
    field: 'accessCodeEffectiveDate',
    colId: 'accessCodeEffectiveDate',
    minWidth: 260,
  },
  ...address,
  ...personalEnd,
]

export const csvTraineeFields = [
  { label: 'Anonymous ID', value: 'anonymousId' },
  { label: 'First Name', value: 'firstname' },
  { label: 'Last name', value: 'lastname' },
  { label: 'Email', value: 'email' },
  createCohortsNamesCsvColumn('cohortNames'),
  csvTraineeStatusColumn,
  {
    label: 'Team',
    value: 'teamCount',
  },
  createTeamRoleCsvColumn(),
  {
    label: 'Survey Monkey - Education',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ highestDegree }: any) => {
      if (isDefined(highestDegree)) {
        return (
          highestDegreeOptions.find((it) => it.value === highestDegree)?.tableValue || 'Unknown'
        )
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Survey Monkey - Race',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ racialDesignation }: any) => {
      if (isDefined(racialDesignation)) {
        return (
          racialDesignationOptions.find((it) => it.value === racialDesignation)?.tableValue ||
          'Unknown'
        )
      }
      return PLACEHOLDER
    },
  },
  { label: 'Survey Monkey - Job title', value: 'jobTitle' },
  { label: 'Survey Monkey - Age', value: 'surveyMonkeyAge' },
  { label: 'Survey Monkey - Current Age', value: 'surveyMonkeyCurrentAge' },
  {
    label: 'Survey Monkey - Sex',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ surveyMonkeySex, otherSurveyMonkeySex }: any) => {
      if (isDefined(surveyMonkeySex)) {
        if (surveyMonkeySex === EUserSex.other && otherSurveyMonkeySex) {
          return otherSurveyMonkeySex
        }
        return sexOptions.find((item) => item.value === surveyMonkeySex)?.tableValue || 'Unknown'
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Survey Monkey - Date',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ surveyMonkeyDate }: any) => {
      if (surveyMonkeyDate === null || surveyMonkeyDate === undefined) {
        return PLACEHOLDER
      }
      return formatDate(surveyMonkeyDate)
    },
  },
  {
    label: 'Last Notification',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ lastNotificationSentAt }: any) => {
      if (!lastNotificationSentAt) return PLACEHOLDER

      return formatDate(lastNotificationSentAt)
    },
  },
  {
    label: 'Notification Settings',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ notificationDelivery }: any) => formatNotificationSettings(notificationDelivery),
  },
  { label: 'Total Time Online', value: 'totalEngagement' },
  { label: 'Institutional Access Code', value: 'accessCodeCode' },
  { label: 'Organization Type', value: 'accessCodeOrganizationType' },
  {
    label: 'Training Pathway',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ trainingPathwayTypeName }: any) => {
      if (isDefined(trainingPathwayTypeName)) {
        const specialPathways = ['Trainee 1A', 'Trainee 2A', 'Trainee 1B', 'Trainee 2B']
        if (specialPathways.includes(trainingPathwayTypeName)) {
          return trainingPathwayTypeName.replace('Trainee ', '')
        }
        return trainingPathwayTypeName
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Years of Experience',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ experience }: any) => {
      if (isDefined(experience)) {
        return yearsOfExperience.find((it) => it.value === experience)?.label || 'Unknown'
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Education',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ education }: any) => {
      if (isDefined(education)) {
        return educationOptions.find((it) => it.value === education)?.label || 'Unknown'
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Language Preference',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ language }: any) => {
      return language ? capitalizeFirstLetter(language) : PLACEHOLDER
    },
  },
  { label: 'Access Code Expiry Date', value: 'accessCodeExpiryDate' },
  {
    label: 'Access Code Effective Date',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ accessCodeEffectiveDate }: any) => {
      if (accessCodeEffectiveDate === null || accessCodeEffectiveDate === undefined) {
        return PLACEHOLDER
      }

      return formatDate(accessCodeEffectiveDate)
    },
  },
  { label: 'Street Address', value: 'userBasicInfo.street' },
  { label: 'ZIP Code', value: 'userBasicInfo.zipCode' },
  { label: 'City', value: 'userBasicInfo.city' },
  { label: 'State', value: 'userBasicInfo.state' },
  { label: 'Country', value: 'userBasicInfo.country' },
  { label: 'Mobile Phone Number', value: 'userBasicInfo.phoneNumber' },
  { label: 'Date of Birth', value: 'userBasicInfo.dateOfBirth' },
  { label: 'Sex', value: 'userBasicInfo.gender' },
]

const adminsColumnDefs: ColDef[] = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  ...personalStart,
  { headerName: 'Mobile Phone Number', field: 'phoneNumber', sortable: false, minWidth: 200 },
  { headerName: 'Role', field: 'type', sortable: false },
  userStatusColumn,
  {
    headerName: 'Creation Date',
    field: 'createdAt',
    sortable: false,
    cellRenderer: columnRenderers.createdAt,
  },
  {
    headerName: 'Created By',
    field: 'createdBy',
    sortable: false,
    cellRenderer: columnRenderers.createdByRenderer,
  },
  {
    headerName: 'Edited By',
    field: 'editedByUser',
    sortable: false,
    cellRenderer: columnRenderers.createdByRenderer,
  },
  {
    headerName: 'Last Edited',
    field: 'updatedAt',
    sortable: false,
    cellRenderer: columnRenderers.createdAt,
  },
]

const orgUsersColumnDefs = [
  { headerName: 'Anonymous ID', field: 'anonymousId', minWidth: 174 },
  ...personalStart,
  {
    headerName: 'Log-in',
    field: 'hasLoggedIn',
    minWidth: 100,
    cellRenderer: (props: ICellRendererParams<{ hasLoggedIn?: boolean }>) => {
      const hasLoggedIn = props.data?.hasLoggedIn
      if (!isDefined(hasLoggedIn)) return PLACEHOLDER
      return hasLoggedIn ? 'Yes' : 'No'
    },
  },
  {
    headerName: 'Organizational Name',
    field: 'organizationName',
    filter: 'agTextColumnFilter',
    minWidth: 420,
  },
  {
    headerName: 'Organization Type',
    field: 'organizationType',
    minWidth: 220,
  },
  {
    headerName: 'Role',
    colId: 'type',
    cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
      const type = props.data?.type
      if (isDefined(type)) {
        const role = type[0]
        return UserTypeLabels[role] || PLACEHOLDER
      }
      return PLACEHOLDER
    },
  },
  userStatusColumn,
  createCohortsNamesColumn('cohortNames'),
  {
    headerName: 'Number of Users (sum)',
    field: 'totalNumberOfUsersOfAccessCodes',
    minWidth: 240,
  },
  {
    headerName: 'Institutional Assess Codes',
    field: 'totalOfAccessCodes',
    minWidth: 250,
    cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
      const ownedAccessCodes = props.data?.ownedAccessCodes
      if (ownedAccessCodes?.length) {
        return <UserOrgAccessCodesCell accessCodes={ownedAccessCodes} />
      }
      return PLACEHOLDER
    },
  },
  {
    headerName: 'Last Notification',
    field: 'lastNotificationSentAt',
    cellRenderer: dateRenderer('lastNotificationSentAt', 'MM/DD/YY'),
    minWidth: 200,
  },
  {
    headerName: 'Notification Settings',
    field: 'notificationDelivery',
    cellRenderer: notificationSettingsCellRenderer,
    minWidth: 280,
  },
  {
    headerName: 'Created on',
    cellRenderer: columnRenderers.createdAt,
    colId: 'createdAt',
  },
  {
    headerName: 'Effective Date',
    cellRenderer: dateRenderer('minEffectiveDateOfAccessCodes'),
    colId: 'minEffectiveDateOfAccessCodes',
  },
  {
    headerName: 'Expiration Date',
    cellRenderer: dateRenderer('maxExpiryDateOfAccessCodes'),
    colId: 'maxExpiryDateOfAccessCodes',
  },
]

export const csvOrgUserFields = [
  { label: 'First Name', value: 'firstname' },
  { label: 'Last name', value: 'lastname' },
  { label: 'Email', value: 'email' },
  {
    label: 'Log-in',
    value: ({ hasLoggedIn }: OrganizationalUser) => {
      if (!isDefined(hasLoggedIn)) return PLACEHOLDER
      return hasLoggedIn ? 'Yes' : 'No'
    },
  },
  { label: 'Organizational Name/State name /Unit name', value: 'organizationName' },
  { label: 'Organization Type', value: 'organizationType' },
  {
    label: 'Role',
    value: ({ type }: OrganizationalUser) => {
      if (isDefined(type)) {
        const role = type[0]
        return UserTypeLabels[role]
      }
      return PLACEHOLDER
    },
  },
  csvUserStatusColumn,
  createCohortsNamesCsvColumn('cohortNames'),
  { label: 'Number of Users (sum)', value: 'totalNumberOfUsersOfAccessCodes' },
  {
    label: 'Institutional Assess Codes',
    value: ({ ownedAccessCodes }: OrganizationalUser) => {
      if (ownedAccessCodes?.length) {
        return ownedAccessCodes
          .map(
            ({ code, effectiveDate, expiryDate, numberOfUsers }) =>
              `(${code}, ${effectiveDate}, ${expiryDate}, ${numberOfUsers})`,
          )
          .join(', ')
      }
      return PLACEHOLDER
    },
  },
  {
    label: 'Last Notification',
    value: ({ lastNotificationSentAt }: OrganizationalUser) => {
      if (!lastNotificationSentAt) return PLACEHOLDER
      return formatDate(lastNotificationSentAt, 'MM/DD/YY')
    },
  },
  {
    label: 'Notification Settings',
    value: ({ notificationDelivery }: OrganizationalUser) =>
      formatNotificationSettings(notificationDelivery ?? undefined),
  },
  { label: 'Created on', value: ({ createdAt }: OrganizationalUser) => formatDate(createdAt) },
  { label: 'Effective Date', value: 'minEffectiveDateOfAccessCodes' },
  { label: 'Expiration Date', value: 'maxExpiryDateOfAccessCodes' },
]

export const getUserColumnDefs = (userType: string) => {
  switch (userType) {
    case UserTypeTab.TRAINEE:
      return traineeColumnDefs
    case UserTypeTab.CAREGIVER:
      return caregiverColumnDefs
    case UserTypeTab.ADMIN:
      return adminsColumnDefs
    case UserTypeTab.ORG:
      return orgUsersColumnDefs
  }
  return personalStart
}
